import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["path", "anchorLink", "isBeta"],
  _excluded2 = ["products", "packs"];
import PortalIdParser from 'PortalIdParser';
import enviro from 'enviro';
import { stringify } from 'hub-http/helpers/params';
import { buildUrl, parseUrl } from 'hub-http/helpers/url';
import { getFullUrl } from 'hubspot-url-utils';
import devLogger from 'react-utils/devLogger';
export const createExternalUrl = (path = '', query, subdomain = 'app') => {
  const {
    protocol,
    hostname
  } = parseUrl(getFullUrl(subdomain));
  const urlOptions = {
    protocol,
    hostname,
    path,
    query: stringify(query || {})
  };
  return buildUrl(urlOptions);
};
export const getInterstitialV2Url = (purchaseMotion, query = {}, location = window.location, isHsocV2 = false) => {
  const isLocal = !enviro.deployed();
  const isOnPricingPage = location.pathname.includes('pricing');
  const shouldUseLocal = isLocal && isOnPricingPage;
  const portalId = PortalIdParser.get();
  const pathByMotion = {
    BUY_ADDITIONAL_SEATS: `/pricing-interstitial/${portalId}/seats`,
    BUY_ADDITIONAL_CONTACTS: `/pricing-interstitial/${portalId}/contacts`,
    BUY_TOUCHLESS: `/pricing-interstitial/${portalId}/purchase`
  };
  /*
   * HSOC V2 add-seats routes to the HSOC-specific manage interstitial
   * (HsocV2ManagePage) instead of the standard seats page.
   *
   * TODO(#211): replace with a context-enum resolver so each context maps its
   * own motion→path table here instead of accumulating more flag params.
   * https://github.com/HubSpotEngineering/PricingPageIssues/issues/211
   */
  const path = isHsocV2 && purchaseMotion === 'BUY_ADDITIONAL_SEATS' ? `/pricing-interstitial/${portalId}/manage` : pathByMotion[purchaseMotion];
  if (!path) {
    throw new Error(`Unsupported purchase motion: ${purchaseMotion}`);
  }
  const {
    protocol,
    hostname
  } = shouldUseLocal ? {
    protocol: 'https',
    hostname: location.hostname
  } : parseUrl(getFullUrl('app'));
  return buildUrl({
    protocol,
    hostname,
    path,
    query: stringify(query || {})
  });
};
export const getCheckoutUrl = (purchaseOrderId, query = {}) => {
  // Allows redirect to `local.hubspotqa` if environment is local, user is on pricing page, and localStorage set.
  const isLocal = !enviro.deployed();
  const isOnPricingPage = window.location.pathname.includes('pricing');
  let forceLocalRedirect = false;
  try {
    forceLocalRedirect = localStorage.getItem('CHECKOUT_REDIRECT_TO_LOCAL') === 'true';
  } catch (err) {
    forceLocalRedirect = false;
  }
  const shouldUseLocal = isLocal && isOnPricingPage && forceLocalRedirect;
  const url = createExternalUrl(`/checkout/${PortalIdParser.get()}/purchases/${purchaseOrderId}`, query, shouldUseLocal ? 'local' : 'app');
  return url;
};
const baseUrlOptions = () => {
  const isQAProxy = window.location.hostname.indexOf('wthubspot') >= 0;
  const isQA = enviro.isQa() || isQAProxy;
  const baseUrl = getFullUrl('app', {
    envOverride: isQA ? 'qa' : 'prod'
  });
  const {
    protocol,
    hostname
  } = parseUrl(baseUrl);
  return {
    protocol,
    hostname
  };
};
export const createPricingPageLink = (_ref = {}) => {
  let {
      path = '',
      anchorLink,
      isBeta = false
    } = _ref,
    params = _objectWithoutPropertiesLoose(_ref, _excluded);
  const portalId = PortalIdParser.get();
  const hasParams = Object.keys(params).length > 0;
  if (hasParams && !params.upgradeSource) {
    devLogger.warn({
      message: `createPricingPageLink: Expected upgradeSource for path ${path} but got ${params.upgradeSource}`,
      key: `createPricingPageLink: ${path}`
    });
  }
  const urlOptions = Object.assign({}, baseUrlOptions(), {
    path: `/${portalId ? 'pricing' : 'public-pricing'}${isBeta ? '-beta' : ''}/${portalId ? `${portalId}/` : ''}${path}`,
    query: hasParams ? stringify(params) : undefined,
    hash: anchorLink !== null && anchorLink !== void 0 ? anchorLink : ''
  });
  return buildUrl(urlOptions);
};
export const createProductsAndAddonsLink = (path = '', params) => {
  const portalId = PortalIdParser.get();
  return buildUrl(Object.assign({}, baseUrlOptions(), {
    path: `/browse/${portalId}/${path}`,
    query: params && stringify(params)
  }));
};
export const navigateTo = (url, openInNewTab = false) => {
  if (window.hubspot.wordpress) {
    window.open(url, '_top');
  } else if (openInNewTab) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }
};
export const getSupportUrl = () => {
  return createExternalUrl(`/support-inbox/${PortalIdParser.get()}?source=in-app-help`);
};
export const getAccountDashboardUrl = () => createExternalUrl(`/account-and-billing/${PortalIdParser.get()}`);
export const getTrialUpgradePageUrl = (apiName, upgradeSource) => {
  const query = {
    apiName
  };
  if (upgradeSource) {
    query.upgradeSource = upgradeSource;
  }
  return buildUrl({
    path: `/trial-pricing/${PortalIdParser.get()}/upgrade`,
    query: stringify(query)
  });
};
export const getUpgradePageUrl = (upgradeSource, params) => {
  return createPricingPageLink(Object.assign({
    path: 'upgrade',
    upgradeSource
  }, params));
};
export const CHECKOUT_PARAMS_KEY = '_checkoutParams';

/*
 * TODO(#211): once the context enum lands, the signature becomes
 * isMotionEligibleForInterstitial(motion, context: PortalContext).
 * https://github.com/HubSpotEngineering/PricingPageIssues/issues/211
 */
export function isMotionEligibleForInterstitial(motion, isSeats, isHsocV2) {
  if (motion === 'BUY_ADDITIONAL_CONTACTS' || motion === 'BUY_ADDITIONAL_SEATS') {
    return true;
  }
  if (motion === 'BUY_TOUCHLESS') {
    return Boolean(isSeats) || isHsocV2;
  }
  return false;
}
export function getRelayedCheckoutParams(searchString = window.location.search) {
  try {
    const raw = new URLSearchParams(searchString).get(CHECKOUT_PARAMS_KEY);
    if (!raw) return undefined;
    return JSON.parse(raw);
  } catch (_unused) {
    return undefined;
  }
}
export function buildInterstitialUrl({
  purchaseMotion,
  apiName,
  purchaseRequest,
  trackingProperties,
  queryParams,
  productsAsQueryParams,
  interstitialQueryParams,
  redirectQueryParams,
  isHsocV2 = false
}) {
  var _ref3;
  const remainingQueryParams = _objectWithoutPropertiesLoose(queryParams, _excluded2);
  const checkoutParamsEntry = redirectQueryParams && Object.keys(redirectQueryParams).length > 0 ? {
    [CHECKOUT_PARAMS_KEY]: JSON.stringify(redirectQueryParams)
  } : {};
  if (purchaseMotion === 'BUY_ADDITIONAL_SEATS' || purchaseMotion === 'BUY_ADDITIONAL_CONTACTS') {
    var _ref2;
    return getInterstitialV2Url(purchaseMotion, Object.assign({}, trackingProperties !== null && trackingProperties !== void 0 && trackingProperties.upgradeSource ? {
      upgradeSource: String(trackingProperties.upgradeSource)
    } : {}, {
      purchaseMotion
    }, remainingQueryParams, (_ref2 = interstitialQueryParams) !== null && _ref2 !== void 0 ? _ref2 : {}, checkoutParamsEntry), undefined, isHsocV2);
  }
  return getInterstitialV2Url(purchaseMotion, Object.assign({
    primaryProduct: apiName
  }, productsAsQueryParams, purchaseRequest.paymentTerm ? {
    paymentTerm: String(purchaseRequest.paymentTerm).toLowerCase()
  } : {}, purchaseRequest.commitmentTerm ? {
    commitmentTerm: String(purchaseRequest.commitmentTerm).toLowerCase()
  } : {}, trackingProperties !== null && trackingProperties !== void 0 && trackingProperties.upgradeSource ? {
    upgradeSource: String(trackingProperties.upgradeSource)
  } : {}, {
    purchaseMotion
  }, remainingQueryParams, purchaseRequest.currencyCode ? {
    currencyCode: String(purchaseRequest.currencyCode)
  } : {}, (_ref3 = interstitialQueryParams) !== null && _ref3 !== void 0 ? _ref3 : {}, checkoutParamsEntry), undefined, isHsocV2);
}